import { useTranslation } from "react-i18next";
import Navbar2 from "../components/Navbar2";
import Footer from "../components/Footer";
import HeroPages from "../components/HeroPages";
import { Link } from "react-router-dom";
import Testimonials from "../components/home/Testimonials";

function WhyChooseUs() {
  const { t } = useTranslation();

  const features = [
    {
      icon: "fa-solid fa-shield-halved",
      title: t("comprehensive_insurance"),
      description: t("comprehensive_insurance_desc"),
      color: "from-blue-500 to-blue-600",
      bgColor: "bg-blue-50",
      iconBg: "bg-blue-100",
      textColor: "text-blue-600",
    },
    {
      icon: "fa-solid fa-star",
      title: t("premium_fleet"),
      description: t("premium_fleet_desc"),
      color: "from-yellow-500 to-yellow-600",
      bgColor: "bg-yellow-50",
      iconBg: "bg-yellow-100",
      textColor: "text-yellow-600",
    },
    {
      icon: "fa-solid fa-headset",
      title: t("support_247"),
      description: t("support_247_desc"),
      color: "from-green-500 to-green-600",
      bgColor: "bg-green-50",
      iconBg: "bg-green-100",
      textColor: "text-green-600",
    },
    {
      icon: "fa-solid fa-dollar-sign",
      title: t("best_price"),
      description: t("best_price_desc"),
      color: "from-[#f5272b] to-red-600",
      bgColor: "bg-red-50",
      iconBg: "bg-red-100",
      textColor: "text-[#f5272b]",
    },
    {
      icon: "fa-solid fa-clock",
      title: t("flexible_rental"),
      description: t("flexible_rental_desc"),
      color: "from-purple-500 to-purple-600",
      bgColor: "bg-purple-50",
      iconBg: "bg-purple-100",
      textColor: "text-purple-600",
    },
    {
      icon: "fa-solid fa-location-dot",
      title: t("free_delivery"),
      description: t("free_delivery_desc"),
      color: "from-pink-500 to-pink-600",
      bgColor: "bg-pink-50",
      iconBg: "bg-pink-100",
      textColor: "text-pink-600",
    },
  ];

  const stats = [
    { number: "10K+", label: t("happy_customers"), icon: "fa-solid fa-users" },
    { number: "500+", label: t("vehicles_available"), icon: "fa-solid fa-car" },
    { number: "15+", label: t("years_experience"), icon: "fa-solid fa-award" },
    { number: "99%", label: t("satisfaction_rate"), icon: "fa-solid fa-heart" },
  ];

  const whyChoosePoints = [
    {
      title: t("trusted_by_thousands"),
      description: t("trusted_by_thousands_desc"),
      icon: "fa-solid fa-certificate",
    },
    {
      title: t("licensed_insured"),
      description: t("licensed_insured_desc"),
      icon: "fa-solid fa-id-card",
    },
    {
      title: t("easy_booking"),
      description: t("easy_booking_desc"),
      icon: "fa-solid fa-mobile-screen",
    },
    {
      title: t("flexible_payment"),
      description: t("flexible_payment_desc"),
      icon: "fa-solid fa-credit-card",
    },
  ];

  return (
    <>
      <Navbar2 />
      <HeroPages name={t("why_choose_use")} />

      <div className="relative min-h-screen overflow-hidden bg-gradient-to-b from-white via-slate-50 to-white">
        {/* Hero Section */}
        <section className="relative pt-32 pb-20 overflow-hidden md:pt-40 md:pb-28">
          <div className="absolute inset-0 opacity-5">
            <div className="absolute top-20 left-10 w-96 h-96 bg-[#f5272b] rounded-full blur-3xl"></div>
            <div className="absolute bg-blue-500 rounded-full bottom-20 right-10 w-96 h-96 blur-3xl"></div>
          </div>

          <div className="container relative z-10 px-4 mx-auto md:px-8">
            <div className="max-w-4xl mx-auto space-y-6 text-center">
              <div className="inline-block px-6 py-2 bg-[#f5272b]/10 rounded-full mb-4">
                <span className="text-[#f5272b] font-bold text-sm uppercase tracking-wide">
                  {t("why_choose_wirasana")}
                </span>
              </div>
              <h1 className="text-5xl font-bold leading-tight md:text-6xl lg:text-7xl text-slate-900">
                {t("trusted_partner")}
              </h1>
              <p className="max-w-3xl mx-auto text-xl leading-relaxed md:text-2xl text-slate-600">
                {t("experience_excellence")}
              </p>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="relative py-16 bg-gradient-to-r from-slate-800 to-slate-900">
          <div className="container px-4 mx-auto md:px-8">
            <div className="grid grid-cols-2 gap-8 lg:grid-cols-4">
              {stats.map((stat, idx) => (
                <div key={idx} className="text-center group">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-[#f5272b] rounded-2xl mb-4 group-hover:scale-110 transition-transform duration-300">
                    <i className={`${stat.icon} text-white text-2xl`}></i>
                  </div>
                  <div className="mb-2 text-4xl font-bold text-white md:text-5xl">{stat.number}</div>
                  <div className="text-sm text-slate-300 md:text-base">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="relative py-20 md:py-28">
          <div className="container px-4 mx-auto md:px-8">
            <div className="mb-16 space-y-4 text-center">
              <h2 className="text-4xl font-bold md:text-5xl text-slate-900">{t("what_makes_us_different")}</h2>
              <p className="max-w-2xl mx-auto text-lg md:text-xl text-slate-600">{t("discover_advantages")}</p>
            </div>

            <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
              {features.map((feature, idx) => (
                <div
                  key={idx}
                  className="relative p-8 overflow-hidden transition-all duration-500 bg-white border-2 shadow-lg group rounded-3xl hover:shadow-2xl border-slate-100 hover:border-transparent"
                >
                  <div
                    className={`absolute inset-0 bg-gradient-to-br ${feature.color} opacity-0 group-hover:opacity-100 transition-opacity duration-500`}
                  ></div>
                  <div className="relative z-10">
                    <div
                      className={`w-16 h-16 ${feature.iconBg} rounded-2xl flex items-center justify-center mb-6 group-hover:bg-white/20 transition-all duration-300`}
                    >
                      <i
                        className={`${feature.icon} text-3xl ${feature.textColor} group-hover:text-white transition-colors duration-300`}
                      ></i>
                    </div>
                    <h3 className="mb-4 text-2xl font-bold text-slate-900 group-hover:text-white">{feature.title}</h3>
                    <p className="text-slate-600 group-hover:text-white/90">{feature.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Choose Points */}
        <section className="relative py-20 bg-gradient-to-br from-slate-50 to-white">
          <div className="container px-4 mx-auto md:px-8">
            <div className="max-w-6xl mx-auto">
              <div className="mb-16 text-center">
                <h2 className="text-4xl font-bold md:text-5xl text-slate-900">{t("more_reasons")}</h2>
              </div>

              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                {whyChoosePoints.map((point, idx) => (
                  <div
                    key={idx}
                    className="flex items-start gap-6 p-6 transition-all duration-300 bg-white border shadow-lg rounded-2xl hover:shadow-xl border-slate-100"
                  >
                    <div className="flex-shrink-0 w-14 h-14 bg-gradient-to-br from-[#f5272b] to-red-600 rounded-xl flex items-center justify-center shadow-lg">
                      <i className={`${point.icon} text-white text-xl`}></i>
                    </div>
                    <div className="flex-1">
                      <h3 className="mb-2 text-xl font-bold text-slate-900">{point.title}</h3>
                      <p className="text-slate-600">{point.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <Testimonials />

        {/* CTA Section */}
        <section className="relative py-20 md:py-28">
          <div className="container px-4 mx-auto md:px-8">
            <div className="max-w-4xl mx-auto bg-gradient-to-r from-[#f5272b] to-red-600 rounded-3xl p-12 md:p-16 text-center shadow-2xl">
              <h2 className="mb-6 text-3xl font-bold text-white md:text-4xl lg:text-5xl">{t("cta_title")}</h2>
              <p className="max-w-2xl mx-auto mb-8 text-xl text-white/90">{t("cta_desc")}</p>
              <div className="flex flex-col justify-center gap-4 sm:flex-row">
                <Link
                  to="/"
                  className="inline-flex items-center justify-center gap-3 px-8 py-4 bg-white text-[#f5272b] font-bold text-lg rounded-2xl shadow-xl hover:shadow-2xl hover:scale-105 transition-all duration-300"
                >
                  {t("book_now")} <i className="fa-solid fa-arrow-right"></i>
                </Link>
                <a
                  href="tel:0361286066"
                  className="inline-flex items-center justify-center gap-3 px-8 py-4 text-lg font-bold text-white transition-all duration-300 shadow-xl bg-slate-900 rounded-2xl hover:shadow-2xl hover:scale-105"
                >
                  <i className="fa-solid fa-phone"></i>
                  {t("call_us_now")}
                </a>
              </div>
            </div>
          </div>
        </section>
      </div>
      <Footer />
    </>
  );
}

export default WhyChooseUs;
